import React, { useMemo, useState, useEffect, useRef } from "react";
import StandardModal from "../../../components/modales/StandardModal";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { EmptyState, StatCard, type DataTableColumn } from "@routes-plan/ui";
import axios from "axios";
import moment from "moment-timezone";
import { formatCurrency } from "../../../utils/format/currency";
import { nowOperationalDateString, operationalTimezone } from '../../../utils/operationalDateTime';
import { useT } from '../../../i18n';
import { ModalTable, ReportStats } from '../components/ReportLayout';

interface ModalOfficeExpenseProps {
  open: boolean;
  onClose: () => void;
  showAlert: (message: string, severity?: "success" | "info" | "warning" | "error") => void;
  showLoading: () => void;
  hideLoading: () => void;
  clientData?: {
    cashBoxId: number;
    date: string;
    dateISO?: string;
    isOutflow?: boolean;
    routeName: string;
    routeId?: number | string;
    typeOperation: number;
  } | null;
}


const OfficeExpenseModal: React.FC<ModalOfficeExpenseProps> = ({
  open,
  onClose,
  clientData,
  showAlert,
}) => {
  const t = useT();
  const [movement, setMovement] = useState<any[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  const showAlertRef = useRef(showAlert);
  showAlertRef.current = showAlert;

  /** Una fila de Resumen Día = un día operativo; alineado con consolidado (startDate === endDate). */
  const dateNorm = useMemo(() => {
    if (!clientData) return "";
    const m = moment(
      clientData.dateISO || clientData.date,
      ["YYYY-MM-DD", "YYYY/MM/DD", moment.ISO_8601],
      true
    );
    return m.isValid() ? m.format("YYYY-MM-DD") : "";
  }, [clientData?.dateISO, clientData?.date]);

  const clientKey = useMemo(() => {
    if (!clientData || !dateNorm) return "";
    return [
      clientData.cashBoxId,
      dateNorm,
      String(clientData.routeId ?? ""),
      clientData.typeOperation,
      clientData.isOutflow ?? false,
    ].join("|");
  }, [clientData?.cashBoxId, dateNorm, clientData?.routeId, clientData?.typeOperation, clientData?.isOutflow]);

  useEffect(() => {
    if (!open) {
      setMovement([]);
      setIsFetching(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !clientData || !clientKey || !dateNorm) return;

    const ac = new AbortController();
    setIsFetching(true);
    setMovement([]);

    const fetchData = async () => {
      try {
        let records: any[] = [];
        const singleDay = true;

        if (clientData.typeOperation === 3 && singleDay) {
          const numericRouteId = Number(clientData.routeId);
          if (!Number.isFinite(numericRouteId) || numericRouteId <= 0) {
            showAlertRef.current(t("cashReports.officeExpense.routeNotFound"), "warning");
            setMovement([]);
            return;
          }
          const { data } = await axios.post(
            "/cash-box-expenses/search",
            { date: dateNorm, routeId: numericRouteId },
            { signal: ac.signal }
          );
          if (Array.isArray(data)) {
            records = Array.isArray(data[0]) ? (data[0] ?? []) : data;
          } else if (Array.isArray(data?.data)) {
            records = data.data;
          }
        } else {
          const formData = {
            cashBoxId: clientData.cashBoxId,
            startDate: dateNorm,
            endDate: dateNorm,
            routeId: String(clientData.routeId ?? ""),
            typeOperation: clientData.typeOperation,
            isOutflow: clientData.isOutflow ?? false,
          };
          const { data } = await axios.post("/reports/consolidated-day/expenses", formData, {
            signal: ac.signal,
          });
          records = Array.isArray(data?.data) ? data.data : [];
        }

        if (ac.signal.aborted) return;

        const filtered = records.filter(Boolean);
        setMovement(filtered);
        if (filtered.length === 0) {
          showAlertRef.current(t("cashReports.officeExpense.noResults"), "info");
        } else {
          showAlertRef.current(
            filtered.length === 1
              ? t("cashReports.officeExpense.foundOne")
              : t("cashReports.officeExpense.foundMany", { count: filtered.length }),
            "success"
          );
        }
      } catch (error: unknown) {
        if (ac.signal.aborted) return;
        console.error("Error al cargar datos del modal:", error);
        showAlertRef.current(t("cashReports.officeExpense.loadError"), "error");
        setMovement([]);
      } finally {
        if (!ac.signal.aborted) {
          setIsFetching(false);
        }
      }
    };

    fetchData();

    return () => {
      ac.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientKey]);

  const rows = useMemo(() => {
    return (Array.isArray(movement) ? movement : []).map((data, index) => {
      const rawDateTime = data?.createdAt || data?.date || null;
      const dateMoment = rawDateTime ? moment.tz(rawDateTime, operationalTimezone()) : null;
      const fallbackDate = data?.date ? moment(data.date).format("YYYY/MM/DD") : "";
      return {
        id: String(data?.id ?? `${String(rawDateTime || "")}-${String(data?.description || "")}-${index}`),
        date: dateMoment?.isValid() ? dateMoment.format("YYYY/MM/DD") : fallbackDate,
        hour: dateMoment?.isValid() ? dateMoment.format("HH:mm") : "",
        value: Number(data?.value) || 0,
        type: String(data?.expenseType?.name || data?.type || ""),
        description: String(data?.description || ""),
      };
    });
  }, [movement]);

  const totalValue = rows.reduce((sum, item) => sum + (Number(item.value) || 0), 0);

  type MovementRow = (typeof rows)[number];
  const columns: DataTableColumn<MovementRow>[] = [
    { id: "date", header: t("common.date"), value: (r) => r.date, sortable: true },
    { id: "hour", header: t("cashReports.officeExpense.hour"), value: (r) => r.hour, sortable: true },
    { id: "value", header: t("cashReports.value"), value: (r) => r.value, render: (r) => formatCurrency(r.value), csv: (r) => formatCurrency(r.value), align: "right", sortable: true },
    { id: "type", header: t("cashReports.officeExpense.type"), value: (r) => r.type, sortable: true },
    { id: "description", header: t("cashReports.description"), value: (r) => r.description, wrap: true },
  ];

  return (
    <StandardModal
      open={open}
      onClose={onClose}
      title={t("cashReports.officeExpense.movementsOf", {
        name:
          clientData?.typeOperation === 3
            ? t("cashReports.officeExpense.wholeRoute")
            : clientData?.routeName || t("summaryReports.shared.route"),
      })}
      subtitle={`${t("cashReports.officeExpense.dateLabel")} ${dateNorm || clientData?.date || "—"}`}
      loading={isFetching}
      maxWidth={1000}
      actions={
        <>
          <span />
          <Button variant="outlined" onClick={onClose}>
            {t("common.close")}
          </Button>
        </>
      }
    >
      {rows.length > 0 ? (
        <Box sx={{ display: "grid", gap: 2, minWidth: 0 }}>
          <ReportStats>
            <StatCard label={t("cashReports.officeExpense.count")} value={rows.length} />
            <StatCard label={t("cashReports.total")} value={formatCurrency(totalValue)} />
          </ReportStats>
          <ModalTable
            rows={rows}
            columns={columns}
            getRowId={(r) => r.id}
            exportFileName={t("cashReports.officeExpense.exportFileName", { date: nowOperationalDateString() })}
            ariaLabel={t("cashReports.officeExpense.title")}
          />
        </Box>
      ) : isFetching ? null : (
        <EmptyState dense title={t("cashReports.officeExpense.noResults")} />
      )}
    </StandardModal>
  );
};

export default OfficeExpenseModal;
